import { ref, computed, onMounted, onUnmounted, watch, nextTick, onBeforeUnmount } from 'vue'
import { useVehiclesStore } from '~/stores/vehicles'
import { useDebounceFn, useWindowFocus } from '@vueuse/core'

// Define default options
const defaultOptions = {
  itemsPerPage: 12, // Default items per page
  debounceWait: 300, // Default debounce time in ms
  debug: false, // Default debug logging
  scrollTarget: null as null | HTMLElement | { value: HTMLElement | null }, // Default scroll target
  distance: 200, // Default distance from bottom to trigger load
  dataSource: null, // Default data source (can be store or custom function)
  fetchMethod: null, // Custom fetch method for getting more data
  identifier: 'default', // Identifier for storage and debugging
  storageKey: 'infiniteScroll', // Storage key prefix for saving state
  enableUrlSync: true, // Whether to sync with URL parameters
  cacheItems: true, // Whether to cache loaded items
  resetOnDataChange: true, // Whether to reset when data source changes
  immediate: true, // Whether to load immediately on mount
  cacheTrimThreshold: 200, // Max items to keep before trimming
  enableCacheTrimming: true, // Enable cache trimming for better memory management
  retainWindowSize: 3, // Number of pages to keep in memory before and after visible page
}

// Define types for better type checking
export interface InfiniteScrollOptions {
  itemsPerPage?: number;
  debounceWait?: number;
  debug?: boolean;
  scrollTarget?: null | HTMLElement | { value: HTMLElement | null } | Window;
  distance?: number;
  dataSource?: any;
  fetchMethod?: Function | null;
  identifier?: string;
  storageKey?: string;
  enableUrlSync?: boolean;
  cacheItems?: boolean;
  resetOnDataChange?: boolean;
  immediate?: boolean;
  onItemsLoaded?: (items: any[]) => void;
  cacheTrimThreshold?: number;
  enableCacheTrimming?: boolean;
  retainWindowSize?: number;
}

export function useInfiniteScrollCustom(options: InfiniteScrollOptions = {}) {
  const mergedOptions = { ...defaultOptions, ...options }
  const { 
    itemsPerPage, 
    debounceWait, 
    debug, 
    scrollTarget, 
    distance,
    dataSource,
    fetchMethod,
    identifier,
    enableUrlSync,
    cacheItems,
    resetOnDataChange,
    immediate,
    cacheTrimThreshold,
    enableCacheTrimming,
    retainWindowSize
  } = mergedOptions
  
  // Track window visibility for performance optimization
  const windowFocus = useWindowFocus();
  const isFocused = computed(() => windowFocus.value);
  
  // Additional state for memory management
  const visiblePage = ref(1);
  const trimActive = ref(false);
  const gcTimeout = ref<number | null>(null);
  const scrollListenerAttached = ref(false);
  
  // Use provided dataSource or default to vehicles store
  const dataSourceRef = dataSource || useVehiclesStore()
  
  const debugLog = (...args: any[]) => {
    if (debug) {
      // console.log(`[useInfiniteScroll:${identifier}]`, ...args)
    }
  }
  
  const page = ref(1)
  const loading = ref(false)
  const allItemsLoaded = ref(false)
  const scrollContainer = ref<HTMLElement | null>(null)
  const error = ref<string | null>(null)
  const cachedItems = ref<any[]>([])

  // Get data depending on the data source
  const allItems = computed(() => {
    // If data source has filteredItems or filteredVehicles property
    if (dataSourceRef.filteredItems) {
      return dataSourceRef.filteredItems
    } else if (dataSourceRef.filteredVehicles) {
      return dataSourceRef.filteredVehicles
    } else if (dataSourceRef.items) {
      return dataSourceRef.items
    } else if (Array.isArray(dataSourceRef)) {
      return dataSourceRef
    }
    // Default fallback
    return []
  })

  // Calculate total items based on the data source
  const totalItems = computed(() => allItems.value.length)

  // Calculate total pages based on total items
  const totalPages = computed(() => Math.ceil(totalItems.value / itemsPerPage))
  
  // Paginate the items locally
  const paginatedItems = computed(() => {
    const end = page.value * itemsPerPage;
    
    if (debug) {
      // console.log(`[useInfiniteScroll:${identifier}] Calculating paginated items - page: ${page.value}, itemsPerPage: ${itemsPerPage}, end: ${end}, available items: ${allItems.value.length}`);
    }
    
    // ALWAYS slice from the full allItems list for the reactive 'items' output.
    // The 'cachedItems' and 'trimCache' are for internal memory management of the 'allItems' source,
    // OR for a more advanced virtual scrolling implementation if you build one.
    // For a standard v-for, the 'items' prop must be a continuous list from the start.
    const result = allItems.value.slice(0, end);
    
    if (debug) {
      // console.log(`[useInfiniteScroll:${identifier}] Returning ${result.length} items from allItems slice`);
    }
    
    return result;
  })

  // Check if all items are loaded based on local pagination
  watch(paginatedItems, (newItems) => {
    const previousValue = allItemsLoaded.value
    allItemsLoaded.value = newItems.length >= totalItems.value
    
    if (debug) {
      // console.log(`[useInfiniteScroll:${identifier}] Items updated:`, {
      //   itemsCount: newItems.length,
      //   totalItems: totalItems.value,
      //   allItemsLoaded: allItemsLoaded.value,
      //   previousAllItemsLoadedValue: previousValue,
      //   itemsMatchTotal: newItems.length === totalItems.value
      // })
    }
    
    debugLog(`Items updated. Current count: ${newItems.value?.length}, Total items: ${totalItems.value}, All loaded: ${allItemsLoaded.value}`)
  })

  // Reset pagination when data source changes
  watch(() => {
    // Watch appropriate property based on data source type
    if (dataSourceRef.activeFilters) {
      return dataSourceRef.activeFilters
    } else if (dataSourceRef.filters) {
      return dataSourceRef.filters
    }
    // Default to the whole data source
    return dataSourceRef
  }, () => {
    if (!resetOnDataChange) return
    
    debugLog('Data source changed, resetting pagination.')
    page.value = 1
    allItemsLoaded.value = false
    
    if (cacheItems) {
      cachedItems.value = []
    }
    
    // Ensure calculation happens after reset
    nextTick(() => {
      if (cacheItems) {
        // Update cache
        cachedItems.value = allItems.value.slice(0, page.value * itemsPerPage)
      }
      
      allItemsLoaded.value = paginatedItems.value.length >= totalItems.value
      debugLog(`After filter reset. Current count: ${paginatedItems.value.length}, Total items: ${totalItems.value}, All loaded: ${allItemsLoaded.value}`)
    });
  }, { deep: true })

  const loadMore = async () => {
    debugLog(`Attempting to load more. Current page: ${page.value}, Total pages: ${totalPages.value}, All items loaded: ${allItemsLoaded.value}`)
    
    // Enhanced debugging
    if (debug) {
      // console.log(`[useInfiniteScroll:${identifier}] LoadMore called:`, {
      //   currentPage: page.value,
      //   totalPages: totalPages.value,
      //   totalItems: totalItems.value,
      //   currentItems: paginatedItems.value.length,
      //   isLoading: loading.value,
      //   allItemsLoaded: allItemsLoaded.value,
      //   dataSourceLoaded: dataSourceRef.isDataLoaded !== undefined ? dataSourceRef.isDataLoaded : 'N/A',
      //   totalSourceItems: Array.isArray(dataSourceRef) ? dataSourceRef.length : 
      //     (dataSourceRef.items?.length || dataSourceRef.vehicles?.length || 'Unknown')
      // })
    }
    
    if (loading.value || allItemsLoaded.value) {
      debugLog('Load more aborted. Already loading or all items loaded.')
      return
    }
    
    // Check if we need to fetch initial data
    if (dataSourceRef.isDataLoaded !== undefined && !dataSourceRef.isDataLoaded) {
      debugLog('Initial data not loaded, fetching...')
      loading.value = true
      error.value = null
      try {
        // Call appropriate fetch method based on data source type
        if (dataSourceRef.fetchVehicles) {
          await dataSourceRef.fetchVehicles()
        } else if (dataSourceRef.fetchItems) {
          await dataSourceRef.fetchItems()
        } else if (fetchMethod) {
          await fetchMethod()
        }
        debugLog('Initial data fetched successfully.')
      } catch (err) {
        // console.error(`[useInfiniteScroll:${identifier}] Error fetching initial data in loadMore:`, err)
        error.value = err instanceof Error ? err.message : 'Failed to fetch initial data'
        loading.value = false
        return
      } finally {
        loading.value = false
      }
    }
    
    // Check if there are more pages to load locally
    if (page.value < totalPages.value) {
      loading.value = true // Simulate loading for pagination increment
      debugLog(`Incrementing page to ${page.value + 1}`)
      const oldPage = page.value
      page.value += 1
      
      if (debug) {
        // console.log(`[useInfiniteScroll:${identifier}] Page incremented: ${oldPage} -> ${page.value}`)
      }
      
      // If using cache, update the cache
      if (cacheItems) {
        cachedItems.value = allItems.value.slice(0, page.value * itemsPerPage)
      }
      
      // No actual data fetching here, pagination is computed
      // Simulate async operation end
      await nextTick(); 
      
      if (debug) {
        // console.log(`[useInfiniteScroll:${identifier}] After page increment: items count = ${paginatedItems.value.length}`)
      }
      
      loading.value = false
      
      // Call onItemsLoaded callback if provided
      if (mergedOptions.onItemsLoaded) {
        const newItems = paginatedItems.value.slice((oldPage - 1) * itemsPerPage)
        mergedOptions.onItemsLoaded(newItems)
      }

      // Potentially re-evaluate scroll conditions shortly after new items are supposedly rendered
      if (!allItemsLoaded.value) { 
        nextTick(() => {
          setTimeout(() => {
            // if (debug) {
              // console.log(`[useInfiniteScroll DEBUG ${identifier}] Triggering post-loadMore scroll check.`);
            // }
            handleScroll(); // Non-debounced, direct call
          }, 50); 
        });
      }

    } else {
      debugLog('No more pages to load locally.')
      
      if (debug) {
        // console.log(`[useInfiniteScroll:${identifier}] Reached end: page ${page.value} of ${totalPages.value}`)
      }
      
      allItemsLoaded.value = true
    }
  }

  // Add a reset function to completely reset pagination state
  const reset = () => {
    if (debug) {
      // console.log(`[useInfiniteScroll:${identifier}] Resetting pagination state`)
    }
    
    page.value = 1
    allItemsLoaded.value = false
    
    if (cacheItems) {
      cachedItems.value = []
    }
    
    // Re-evaluate after reset
    nextTick(() => {
      if (cacheItems) {
        // Update cache
        cachedItems.value = allItems.value.slice(0, page.value * itemsPerPage)
      }
      
      allItemsLoaded.value = paginatedItems.value.length >= totalItems.value
    })
  }

  // New function to perform cache trimming / garbage collection
  const trimCache = () => {
    if (!enableCacheTrimming || !cacheItems) return;
    
    // Skip trimming if under threshold or currently trimming
    if (cachedItems.value.length <= cacheTrimThreshold || trimActive.value) {
      return;
    }
    
    trimActive.value = true;
    
    try {
      if (debug) {
        // console.log(`[useInfiniteScroll:${identifier}] Trimming cache, current size: ${cachedItems.value.length} items`);
      }
      
      // Calculate range of pages to keep
      const currentPage = visiblePage.value;
      const startPage = Math.max(1, currentPage - retainWindowSize);
      const endPage = currentPage + retainWindowSize;
      
      // Calculate corresponding item indices
      const startIndex = (startPage - 1) * itemsPerPage;
      const endIndex = Math.min(endPage * itemsPerPage, allItems.value.length);
      
      if (debug) {
        // console.log(`[useInfiniteScroll:${identifier}] Keeping items from page ${startPage} to ${endPage} (${startIndex} to ${endIndex})`);
      }
      
      // Rebuild the cache with only items in the retain window
      const trimmedCache = [];
      
      // Add items from active window
      for (let i = startIndex; i < endIndex; i++) {
        if (allItems.value[i]) {
          trimmedCache.push(allItems.value[i]);
        }
      }
      
      // Update cache with trimmed items
      cachedItems.value = trimmedCache;
      
      if (debug) {
        // console.log(`[useInfiniteScroll:${identifier}] Cache trimmed from ${cachedItems.value.length} to ${trimmedCache.length} items`);
      }
    } finally {
      trimActive.value = false;
    }
  };
  
  // Function to update visible page based on scroll position
  const updateVisiblePage = (scrollTop: number, clientHeight: number, itemsPerPageOpt: number, itemHeight: number) => {
    if (itemHeight === 0) return; // Avoid division by zero
    const viewportMiddle = scrollTop + (clientHeight / 2);
    const middleItemIndex = Math.floor(viewportMiddle / itemHeight);
    const estimatedPage = Math.max(1, Math.ceil((middleItemIndex + 1) / itemsPerPageOpt));
    const newVisiblePage = Math.min(estimatedPage, totalPages.value || 1);

    if (visiblePage.value !== newVisiblePage) {
      visiblePage.value = newVisiblePage;
      scheduleGarbageCollection();
    }
  };
  
  // Schedule cache trimming with debounce
  const scheduleGarbageCollection = () => {
    // Clear any existing timeouts
    if (gcTimeout.value !== null) {
      clearTimeout(gcTimeout.value);
    }
    
    // Only schedule GC if trimming is enabled and we have enough items
    if (enableCacheTrimming && cacheItems && cachedItems.value.length > cacheTrimThreshold) {
      // Schedule garbage collection 2 seconds after scrolling stops
      gcTimeout.value = window.setTimeout(() => {
        if (isFocused.value) { // Only run when window is focused
          trimCache();
        }
        gcTimeout.value = null;
      }, 2000);
    }
  };
  
  // Enhanced scroll handler that also updates visible page
  const handleScroll = () => {
    // *** ADDED LOG TO VERIFY DISTANCE/THRESHOLD ***
    const threshold = distance; // options.distance value
    // if (debug) { // Keep debug logs controlled by the composable's debug option
      // console.log(`[useInfiniteScroll:${identifier}] handleScroll event. Configured distance (threshold): ${threshold}, scrollTarget used: ${scrollContainer.value ? 'element' : 'window'}`);
    // }
    // *** END ADDED LOG ***

    let scrollTop, scrollHeight, clientHeight;

    if (scrollContainer.value) { 
      scrollTop = scrollContainer.value.scrollTop;
      scrollHeight = scrollContainer.value.scrollHeight;
      clientHeight = scrollContainer.value.clientHeight;
    } else if (process.client) { 
      scrollTop = window.scrollY;
      scrollHeight = document.documentElement.scrollHeight;
      clientHeight = window.innerHeight;
    } else {
      return; 
    }
    
    currentScrollPosition.value = scrollTop; 
    updateVisiblePage(scrollTop, clientHeight, itemsPerPage, itemHeightEstimate.value); 
    
    const distanceToBottom = scrollHeight - scrollTop - clientHeight;
    const isNearBottom = distanceToBottom < threshold;

    // ADD THIS TEMPORARY LOG for detailed scroll metrics:
    // if (debug) { // Or remove the if(debug) for unconditional logging during this specific test
        // console.log(`[useInfiniteScroll DEBUG ${identifier}] metrics:`, {
        //     scrollTop,
        //     scrollHeight,
        //     clientHeight,
        //     distanceToBottom,
        //     threshold,
        //     isNearBottom,
        //     loading: loading.value,
        //     allItemsLoaded: allItemsLoaded.value,
        //     shouldLoad: isNearBottom && !loading.value && !allItemsLoaded.value
        // });
    // }
        
    if (isNearBottom && !loading.value && !allItemsLoaded.value) {
      if (debug) {
        // console.log(`[useInfiniteScroll:${identifier}] Near bottom detected, triggering load more from scroll handler`);
      }
      loadMore();
    }
  };

  const itemHeightEstimate = ref(120); // Add a ref for item height estimate, can be an option

  const currentScrollPosition = ref(0); // Add this ref for the composable

  const debouncedHandleScroll = useDebounceFn(handleScroll, debounceWait)
  
  // Cleanup function to ensure proper garbage collection
  const cleanup = () => {
    let scrollListenerTarget: HTMLElement | Window = window; // Default to window
    // Determine the actual target the listener was attached to
    if (scrollTarget) {
        const st = scrollTarget;
        const element = st && typeof st === 'object' && 'value' in st ? st.value : st;
        if (element instanceof HTMLElement) {
            scrollListenerTarget = element;
        } else if (element === window) {
            scrollListenerTarget = window;
        }
        // If element is null (meaning scrollTarget was explicitly null to target window), 
        // scrollListenerTarget remains window.
    } // If scrollTarget was not provided at all, it defaults to window.

    if (scrollListenerAttached.value) {
      scrollListenerTarget.removeEventListener('scroll', debouncedHandleScroll);
      scrollListenerAttached.value = false;
      // if (debug) console.log(`[useInfiniteScroll:${identifier}] Cleanup: Removed scroll listener from:`, scrollListenerTarget === window ? 'window' : scrollListenerTarget);
    }
    
    if (gcTimeout.value !== null) {
      clearTimeout(gcTimeout.value);
      gcTimeout.value = null;
      // if (debug) console.log(`[useInfiniteScroll:${identifier}] Cleanup: Cleared GC timeout.`);
    }
    
    if (cacheItems) {
      cachedItems.value = [];
      // if (debug) console.log(`[useInfiniteScroll:${identifier}] Cleanup: Cleared cached items.`);
    }
    
    if (debug) {
      // console.log(`[useInfiniteScroll:${identifier}] Cleanup function executed.`);
    }
  };
  
  // Enhanced observe scroll with tracking
  const observeScroll = () => {
    let targetElement: HTMLElement | null = null;
    let scrollListenerTarget: HTMLElement | Window = window; // Default to window

    if (scrollTarget) {
      const st = scrollTarget; // st is options.scrollTarget
      const element = st && typeof st === 'object' && 'value' in st ? st.value : st;
      if (element instanceof HTMLElement) {
        targetElement = element;
        scrollListenerTarget = element;
        scrollContainer.value = element; // Keep for internal reference if needed
      } else if (element === window) { // Explicitly allow window object
         // scrollListenerTarget is already window, scrollContainer remains null (or documentElement)
         // console.log(`[useInfiniteScroll:${identifier}] Scroll target is window.`);
      } else if (element !== null) {
        // console.error(`[useInfiniteScroll:${identifier}] Invalid scroll target, must be an HTMLElement, ref to HTMLElement, or window. Got:`, element);
        return;
      }
    } // If scrollTarget is null, defaults to window
    
    if (debug) {
      // console.log(`[useInfiniteScroll:${identifier}] Adding scroll listener to:`, scrollListenerTarget === window ? 'window' : scrollListenerTarget);
    }

    scrollListenerTarget.addEventListener('scroll', debouncedHandleScroll, { passive: true });
    scrollListenerAttached.value = true;

    // Initial check in case content is already scrollable and near bottom
    handleScroll(); 
  };
  
  // Lifecycle hooks
  onMounted(() => {
    // Ensure initial data is loaded if needed
    if (dataSourceRef.isDataLoaded !== undefined && !dataSourceRef.isDataLoaded) {
      debugLog('Component mounted, initial data not loaded, fetching...');
      if (immediate) {
        loadMore(); // Trigger initial fetch if necessary
      }
    } else {
      // Recalculate if all items loaded based on current state
      allItemsLoaded.value = paginatedItems.value.length >= totalItems.value
      debugLog('Component mounted, data already loaded.')
      
      // If using cache, initialize it
      if (cacheItems) {
        cachedItems.value = allItems.value.slice(0, page.value * itemsPerPage)
      }
    }
    
    observeScroll()
  })
  
  onUnmounted(() => {
    cleanup()
  })

  onBeforeUnmount(() => { // Also call cleanup here for good measure, esp. with HMR
    cleanup();
  });

  // Return reactive state and methods
  return {
    items: paginatedItems, // Use locally paginated items
    loading,
    allItemsLoaded,
    scrollContainer,
    loadMore, // Expose loadMore if needed externally
    error,
    currentPage: page, 
    totalItems,
    reset, // Expose reset function
    totalPages, // Expose totalPages for UI
    dataSource: dataSourceRef, // Expose data source reference
    options: mergedOptions, // Expose merged options for debugging
    visiblePage, // Now exposing the visible page
    trimCache,  // Expose manual cache trimming function
    currentScrollPosition // Expose for saving if needed
  }
}
